import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from '../lib/supabase';
import { SkeletonRow } from './skeletons';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (val, decimals = 0) =>
  val != null ? val?.toLocaleString('es-ES', { maximumFractionDigits: decimals }) : '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return {
    date: d?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    time: d?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
  };
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.floor((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));
};

const totalDaysInSystem = (createdAt) => {
  if (!createdAt) return null;
  return Math.floor((Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
};

// ─── Event type configs ───────────────────────────────────────────────────────

const EVENT_CONFIG = {
  CREATED: {
    icon: 'PlusCircle',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    label: 'Lote creado',
  },
  STATUS_CHANGE: {
    icon: 'RefreshCw',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    label: 'Cambio de estado',
  },
  PRICE_UPDATE: {
    icon: 'TrendingUp',
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    border: 'border-purple-200',
    dot: 'bg-purple-500',
    label: 'Actualización de precio',
  },
  YIELD_UPDATE: {
    icon: 'Percent',
    color: 'text-teal-600',
    bg: 'bg-teal-100',
    border: 'border-teal-200',
    dot: 'bg-teal-500',
    label: 'Actualización de yield',
  },
  SOLD: {
    icon: 'ShoppingCart',
    color: 'text-emerald-600',
    bg: 'bg-emerald-100',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    label: 'Vendido',
  },
  QUALITY_INCIDENT: {
    icon: 'AlertTriangle',
    color: 'text-red-600',
    bg: 'bg-red-100',
    border: 'border-red-200',
    dot: 'bg-red-500',
    label: 'Incidencia de calidad',
  },
  RISK_OVERRIDE: {
    icon: 'ShieldAlert',
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    border: 'border-orange-200',
    dot: 'bg-orange-500',
    label: 'Precio negociado con riesgo asumido',
  },
};

// ─── Build timeline events from batch + audit_log + sale ──────────────────────

function buildTimeline(batch, auditEvents, sale) {
  const events = [];

  // CREATED event
  events?.push({
    type: 'CREATED',
    ts: batch?.created_at,
    data: {
      maxBuyPrice: parseFloat(batch?.max_buy_price) || null,
      yieldEst: parseFloat(batch?.yield_est) || null,
      expectedMargin: parseFloat(batch?.expected_margin) || null,
      metal: batch?.metal_name,
      category: batch?.category_name,
      weightT: parseFloat(batch?.weight_t) || null,
    },
  });

  // Process audit events
  (auditEvents || [])?.forEach(ev => {
    const oldV = ev?.old_values || {};
    const newV = ev?.new_values || {};

    // STATUS_CHANGE
    if (oldV?.batch_status && newV?.batch_status && oldV?.batch_status !== newV?.batch_status) {
      events?.push({
        type: 'STATUS_CHANGE',
        ts: ev?.created_at,
        actor: ev?.user_email,
        data: { oldStatus: oldV?.batch_status, newStatus: newV?.batch_status },
      });
    }

    // PRICE_UPDATE
    const priceChanged =
      (oldV?.max_buy_price != null && newV?.max_buy_price != null && parseFloat(oldV?.max_buy_price) !== parseFloat(newV?.max_buy_price)) ||
      (oldV?.expected_margin != null && newV?.expected_margin != null && parseFloat(oldV?.expected_margin) !== parseFloat(newV?.expected_margin));
    if (priceChanged) {
      events?.push({
        type: 'PRICE_UPDATE',
        ts: ev?.created_at,
        actor: ev?.user_email,
        data: {
          oldPrice: oldV?.max_buy_price != null ? parseFloat(oldV?.max_buy_price) : null,
          newPrice: newV?.max_buy_price != null ? parseFloat(newV?.max_buy_price) : null,
          oldMargin: oldV?.expected_margin != null ? parseFloat(oldV?.expected_margin) : null,
          newMargin: newV?.expected_margin != null ? parseFloat(newV?.expected_margin) : null,
        },
      });
    }

    // YIELD_UPDATE
    if (oldV?.yield_est != null && newV?.yield_est != null && parseFloat(oldV?.yield_est) !== parseFloat(newV?.yield_est)) {
      events?.push({
        type: 'YIELD_UPDATE',
        ts: ev?.created_at,
        actor: ev?.user_email,
        data: {
          oldYield: parseFloat(oldV?.yield_est),
          newYield: parseFloat(newV?.yield_est),
          delta: parseFloat(newV?.yield_est) - parseFloat(oldV?.yield_est),
        },
      });
    }

    // QUALITY_INCIDENT
    const qualityChanged =
      (oldV?.contamination_risk_score != null && newV?.contamination_risk_score != null && parseFloat(oldV?.contamination_risk_score) !== parseFloat(newV?.contamination_risk_score)) ||
      (oldV?.notes !== newV?.notes && (oldV?.notes || newV?.notes));
    if (qualityChanged && !priceChanged && !(oldV?.batch_status && newV?.batch_status && oldV?.batch_status !== newV?.batch_status)) {
      events?.push({
        type: 'QUALITY_INCIDENT',
        ts: ev?.created_at,
        actor: ev?.user_email,
        data: {
          oldRisk: oldV?.contamination_risk_score,
          newRisk: newV?.contamination_risk_score,
          oldNotes: oldV?.notes,
          newNotes: newV?.notes,
        },
      });
    }

    // RISK_OVERRIDE
    if (ev?.action === 'RISK_OVERRIDE') {
      events?.push({
        type: 'RISK_OVERRIDE',
        ts: ev?.created_at,
        actor: ev?.user_email,
        data: {
          maxBuyPrice: newV?.max_buy_price != null ? parseFloat(newV?.max_buy_price) : (oldV?.max_buy_price != null ? parseFloat(oldV?.max_buy_price) : null),
          actualBuyPrice: newV?.actual_buy_price != null ? parseFloat(newV?.actual_buy_price) : null,
          riskOverrideEurPerTon: newV?.risk_override_eur_per_ton != null ? parseFloat(newV?.risk_override_eur_per_ton) : null,
          riskOverrideBadge: newV?.risk_override_badge || null,
          bufferConsumidoPct: newV?.buffer_consumido_pct != null ? parseFloat(newV?.buffer_consumido_pct) : null,
          puntoEquilibrio: newV?.punto_equilibrio != null ? parseFloat(newV?.punto_equilibrio) : null,
        },
      });
    }
  });

  // SOLD event
  if (sale) {
    const estMargin = parseFloat(batch?.expected_margin) || 0;
    const realMargin = parseFloat(sale?.realized_margin_eur_per_ton) || 0;
    const marginDelta = realMargin - estMargin;
    events?.push({
      type: 'SOLD',
      ts: sale?.sold_at,
      data: {
        salePrice: parseFloat(sale?.sale_price_eur_per_ton) || parseFloat(sale?.sale_price_per_ton) || null,
        realYield: parseFloat(sale?.real_yield_pct) || parseFloat(sale?.actual_yield_pct) || null,
        realizedMargin: realMargin,
        totalMargin: parseFloat(sale?.total_margin_eur) || null,
        buyerName: sale?.buyer_name,
        buyerNotes: sale?.buyer_notes || sale?.notes,
        marginDelta,
        marginDeltaPct: estMargin !== 0 ? (marginDelta / Math.abs(estMargin)) * 100 : null,
      },
    });
  }

  // Sort chronologically
  events?.sort((a, b) => new Date(a?.ts) - new Date(b?.ts));
  return events;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DeltaBadge({ value, unit = '€/t', decimals = 1 }) {
  if (value == null) return null;
  const positive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${
      positive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
    }`}>
      <Icon name={positive ? 'TrendingUp' : 'TrendingDown'} size={10} color="currentColor" />
      {positive ? '+' : ''}{fmt(value, decimals)} {unit}
    </span>
  );
}

function TimelineEvent({ event, isLast }) {
  const cfg = EVENT_CONFIG?.[event?.type] || EVENT_CONFIG?.CREATED;
  const { date, time } = fmtDateTime(event?.ts);

  const renderContent = () => {
    const d = event?.data || {};
    switch (event?.type) {
      case 'CREATED':
        return (
          <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {d?.metal && <InfoChip label="Metal" value={d?.metal} />}
            {d?.category && <InfoChip label="Categoría" value={d?.category} />}
            {d?.weightT != null && <InfoChip label="Peso" value={`${fmt(d?.weightT, 3)} t`} />}
            {d?.maxBuyPrice != null && <InfoChip label="Precio máx." value={`${fmt(d?.maxBuyPrice, 0)} €/t`} />}
            {d?.yieldEst != null && <InfoChip label="Yield est." value={`${fmt(d?.yieldEst, 1)}%`} />}
            {d?.expectedMargin != null && <InfoChip label="Margen est." value={`${fmt(d?.expectedMargin, 1)}%`} />}
          </div>
        );
      case 'STATUS_CHANGE':
        return (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <StatusPill status={d?.oldStatus} />
            <Icon name="ArrowRight" size={12} color="var(--color-muted-foreground)" />
            <StatusPill status={d?.newStatus} />
          </div>
        );
      case 'PRICE_UPDATE':
        return (
          <div className="mt-1.5 space-y-1">
            {d?.oldPrice != null && d?.newPrice != null && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Precio máx.:</span>
                <span className="text-xs font-mono line-through text-slate-400">{fmt(d?.oldPrice, 0)} €/t</span>
                <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
                <span className="text-xs font-mono font-semibold text-slate-700">{fmt(d?.newPrice, 0)} €/t</span>
                <DeltaBadge value={d?.newPrice - d?.oldPrice} unit="€/t" />
              </div>
            )}
            {d?.oldMargin != null && d?.newMargin != null && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Margen est.:</span>
                <span className="text-xs font-mono line-through text-slate-400">{fmt(d?.oldMargin, 1)}%</span>
                <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
                <span className="text-xs font-mono font-semibold text-slate-700">{fmt(d?.newMargin, 1)}%</span>
                <DeltaBadge value={d?.newMargin - d?.oldMargin} unit="%" />
              </div>
            )}
          </div>
        );
      case 'YIELD_UPDATE':
        return (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Yield:</span>
            <span className="text-xs font-mono line-through text-slate-400">{fmt(d?.oldYield, 1)}%</span>
            <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
            <span className="text-xs font-mono font-semibold text-slate-700">{fmt(d?.newYield, 1)}%</span>
            <DeltaBadge value={d?.delta} unit="%" />
          </div>
        );
      case 'SOLD':
        return (
          <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {d?.salePrice != null && <InfoChip label="Precio venta" value={`${fmt(d?.salePrice, 0)} €/t`} />}
            {d?.realYield != null && <InfoChip label="Yield real" value={`${fmt(d?.realYield, 1)}%`} />}
            {d?.realizedMargin != null && <InfoChip label="Margen real" value={`${fmt(d?.realizedMargin, 1)}%`} />}
            {d?.totalMargin != null && <InfoChip label="Margen total" value={`${fmt(d?.totalMargin, 0)} €`} />}
            {d?.buyerName && <InfoChip label="Comprador" value={d?.buyerName} />}
            {d?.marginDelta != null && (
              <div className="bg-slate-50 rounded-lg p-2">
                <p className="text-xs text-slate-500 mb-0.5">Delta margen</p>
                <DeltaBadge value={d?.marginDelta} unit="%" />
              </div>
            )}
          </div>
        );
      case 'QUALITY_INCIDENT':
        return (
          <div className="mt-1.5 space-y-1">
            {d?.oldRisk != null && d?.newRisk != null && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Riesgo contaminación:</span>
                <span className="text-xs font-mono line-through text-slate-400">{fmt(d?.oldRisk, 1)}</span>
                <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
                <span className="text-xs font-mono font-semibold text-red-600">{fmt(d?.newRisk, 1)}</span>
              </div>
            )}
            {d?.newNotes && d?.newNotes !== d?.oldNotes && (
              <p className="text-xs text-slate-600 italic">Nota actualizada: {d?.newNotes}</p>
            )}
          </div>
        );
      case 'RISK_OVERRIDE':
        return (
          <div className="mt-1.5 space-y-2">
            {/* Price delta */}
            {d?.maxBuyPrice != null && d?.actualBuyPrice != null && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Precio máx. calculado:</span>
                <span className="text-xs font-mono text-slate-600">{fmt(d?.maxBuyPrice, 0)} €/t</span>
                <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
                <span className="text-xs font-mono font-semibold text-orange-700">{fmt(d?.actualBuyPrice, 0)} €/t</span>
                <DeltaBadge value={d?.actualBuyPrice - d?.maxBuyPrice} unit="€/t" />
              </div>
            )}
            {/* Risk metrics */}
            <div className="grid grid-cols-2 gap-1.5">
              {d?.riskOverrideEurPerTon != null && (
                <InfoChip label="Riesgo asumido" value={`+${fmt(d?.riskOverrideEurPerTon, 1)} €/t`} />
              )}
              {d?.bufferConsumidoPct != null && (
                <InfoChip label="Buffer consumido" value={`${fmt(d?.bufferConsumidoPct, 1)}%`} />
              )}
              {d?.puntoEquilibrio != null && (
                <InfoChip label="Punto equilibrio" value={`${fmt(d?.puntoEquilibrio, 0)} €/t`} />
              )}
              {d?.riskOverrideBadge && (
                <div className={`rounded-lg p-2 ${
                  d?.riskOverrideBadge === 'CRÍTICO' ? 'bg-red-50 border border-red-100' :
                  d?.riskOverrideBadge === 'ELEVADO'? 'bg-orange-50 border border-orange-100' : 'bg-amber-50 border border-amber-100'
                }`}>
                  <p className="text-xs text-slate-400 mb-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Badge de riesgo</p>
                  <p className={`text-xs font-bold font-mono ${
                    d?.riskOverrideBadge === 'CRÍTICO' ? 'text-red-700' :
                    d?.riskOverrideBadge === 'ELEVADO'? 'text-orange-700' : 'text-amber-700'
                  }`}>{d?.riskOverrideBadge}</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex gap-3">
      {/* Timeline spine */}
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: '28px' }}>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${cfg?.bg} ${cfg?.border}`}>
          <Icon name={cfg?.icon} size={13} color={cfg?.color?.replace('text-', '')?.replace('-600', '')} className={cfg?.color} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1" style={{ minHeight: '20px' }} />}
      </div>
      {/* Content */}
      <div className={`pb-5 flex-1 min-w-0 ${isLast ? '' : ''}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {cfg?.label}
            </p>
            {event?.actor && (
              <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                {event?.actor}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs font-mono text-slate-500">{date}</p>
            <p className="text-xs font-mono text-slate-400">{time}</p>
          </div>
        </div>
        {renderContent()}
      </div>
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg p-2">
      <p className="text-xs text-slate-400 mb-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</p>
      <p className="text-xs font-semibold text-slate-700 font-mono">{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const STATUS_COLORS = {
    'Procesando': 'bg-blue-100 text-blue-700',
    'Listo para Venta': 'bg-emerald-100 text-emerald-700',
    'Completado': 'bg-slate-100 text-slate-600',
    'En Espera': 'bg-amber-100 text-amber-700',
    'Cancelado': 'bg-red-100 text-red-700',
  };
  const STATUS_LABELS = {
    'Completado': 'Vendido',
  };
  const cls = STATUS_COLORS?.[status] || 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
{STATUS_LABELS?.[status] || status}
    </span>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function BatchTimelineModal({ batchId, batchRaw, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(batchRaw || null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [sale, setSale] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const [batchRes, auditRes, saleRes] = await Promise.all([
        supabase?.from('batches')?.select('*')?.eq('id', batchId)?.single(),
        supabase?.from('audit_log')
          ?.select('*')
          ?.eq('entity_type', 'batches')
          ?.eq('entity_id', String(batchId))
          ?.order('created_at', { ascending: true }),
        supabase?.from('sales')
          ?.select('*')
          ?.eq('batch_id', batchId)
          ?.order('sold_at', { ascending: false })
          ?.limit(1),
      ]);
      if (batchRes?.data) setBatch(batchRes?.data);
      setAuditEvents(auditRes?.data || []);
      setSale(saleRes?.data?.[0] || null);
    } catch (err) {
      setError('Error al cargar el historial del lote.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e?.key === 'Escape') onClose?.(); };
    document?.addEventListener('keydown', handler);
    return () => document?.removeEventListener('keydown', handler);
  }, [onClose]);

  const timeline = batch ? buildTimeline(batch, auditEvents, sale) : [];
  const daysTotal = totalDaysInSystem(batch?.created_at);

  // Processing dates
  const createdAt = batch?.created_at;
  const soldAt = sale?.sold_at;
  const statusChanges = auditEvents?.filter(ev => ev?.old_values?.batch_status && ev?.new_values?.batch_status && ev?.old_values?.batch_status !== ev?.new_values?.batch_status);
  const processingStarted = statusChanges?.find(ev => ev?.new_values?.batch_status === 'Procesando')?.created_at;
  const readyForSale = statusChanges?.find(ev => ev?.new_values?.batch_status === 'Listo para Venta')?.created_at;

  const daysCreatedToProcessing = daysBetween(createdAt, processingStarted);
  const daysProcessingToSold = daysBetween(processingStarted, soldAt);
  const daysCreatedToSold = daysBetween(createdAt, soldAt);

  // Margin delta summary
  const estMargin = parseFloat(batch?.expected_margin) || null;
  const realMargin = sale ? (parseFloat(sale?.realized_margin_eur_per_ton) || null) : null;
  const marginDelta = estMargin != null && realMargin != null ? realMargin - estMargin : null;
  const marginDeltaPct = estMargin != null && estMargin !== 0 && marginDelta != null ? (marginDelta / Math.abs(estMargin)) * 100 : null;
  const estYield = parseFloat(batch?.yield_est) || null;
  const realYield = sale ? (parseFloat(sale?.real_yield_pct) || parseFloat(sale?.actual_yield_pct) || null) : null;
  const yieldDelta = estYield != null && realYield != null ? realYield - estYield : null;

  if (!batchId) return null;

  return (
    <div
      className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Historial del lote"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: '"Outfit", sans-serif' }}>
                {batch?.batch_code || (batchId?.slice(0, 8)?.toUpperCase()) || 'Lote'}
              </h2>
              {batch?.metal_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                  {batch?.metal_name}
                </span>
              )}
              {batch?.category_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  {batch?.category_name}
                </span>
              )}
              {batch?.batch_status && (
                <StatusPill status={batch?.batch_status} />
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {batch?.weight_t != null && (
                <span className="text-xs text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <Icon name="Weight" size={11} color="currentColor" className="inline mr-1" />
                  {fmt(parseFloat(batch?.weight_t), 3)} t
                </span>
              )}
              {createdAt && (
                <span className="text-xs text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <Icon name="Calendar" size={11} color="currentColor" className="inline mr-1" />
                  Creado {fmtDate(createdAt)}
                </span>
              )}
              {daysTotal != null && (
                <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  {daysTotal}d en sistema
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <Icon name="X" size={18} color="currentColor" />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cargando historial...</p>
              <table className="w-full">
                <tbody>
                  {Array.from({ length: 5 })?.map((_, i) => (
                    <SkeletonRow key={i} cols={3} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            <div className="p-8 flex flex-col items-center gap-3 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={22} color="#ef4444" />
              </div>
              <p className="text-sm text-slate-600" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{error}</p>
              <button
                onClick={fetchData}
                className="text-sm text-orange-600 hover:underline font-medium"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                Reintentar
              </button>
            </div>
          ) : (
            <div className="p-5 space-y-6">

              {/* ── Timeline Section ── */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <Icon name="Clock" size={13} color="currentColor" />
                  Línea de tiempo
                </h3>
                {timeline?.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Icon name="FileX" size={32} color="var(--color-muted-foreground)" strokeWidth={1.5} />
                    <p className="text-sm text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Sin historial registrado para este lote</p>
                  </div>
                ) : (
                  <div>
                    {timeline?.map((event, idx) => (
                      <TimelineEvent
                        key={`${event?.type}-${event?.ts}-${idx}`}
                        event={event}
                        isLast={idx === timeline?.length - 1}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* ── Margin Delta Summary Card ── */}
              {(estMargin != null || realMargin != null) && (
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    <Icon name="TrendingUp" size={13} color="currentColor" />
                    Resumen de márgenes
                  </h3>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen estimado</p>
                        <p className="text-sm font-bold font-mono text-slate-700">{estMargin != null ? `${fmt(estMargin, 1)}%` : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen realizado</p>
                        <p className={`text-sm font-bold font-mono ${realMargin != null ? (realMargin >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`}>
                          {realMargin != null ? `${fmt(realMargin, 1)}%` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Delta margen</p>
                        {marginDelta != null ? (
                          <div className="flex items-center gap-1">
                            <span className={`text-sm font-bold font-mono ${marginDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {marginDelta >= 0 ? '+' : ''}{fmt(marginDelta, 1)}%
                            </span>
                            {marginDeltaPct != null && (
                              <span className={`text-xs ${marginDeltaPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                ({marginDeltaPct >= 0 ? '+' : ''}{fmt(marginDeltaPct, 1)}%)
                              </span>
                            )}
                          </div>
                        ) : <p className="text-sm text-slate-400 font-mono">—</p>}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Días hasta venta</p>
                        <p className="text-sm font-bold font-mono text-slate-700">{daysCreatedToSold != null ? `${daysCreatedToSold}d` : '—'}</p>
                      </div>
                    </div>
                    {/* Yield accuracy */}
                    {(estYield != null || realYield != null) && (
                      <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-3">
                        <div>
                          <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield estimado</p>
                          <p className="text-sm font-bold font-mono text-slate-700">{estYield != null ? `${fmt(estYield, 1)}%` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield real</p>
                          <p className="text-sm font-bold font-mono text-slate-700">{realYield != null ? `${fmt(realYield, 1)}%` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Precisión yield</p>
                          {yieldDelta != null ? (
                            <span className={`text-sm font-bold font-mono ${Math.abs(yieldDelta) <= 2 ? 'text-emerald-600' : yieldDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {yieldDelta >= 0 ? '+' : ''}{fmt(yieldDelta, 1)} pp
                            </span>
                          ) : <p className="text-sm text-slate-400 font-mono">—</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ── Seller / Supplier Details ── */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <Icon name="Users" size={13} color="currentColor" />
                  Proveedor y comprador
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  {/* Supplier */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Proveedor</p>
                      <p className="text-sm font-medium text-slate-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                        {batch?.supplier_name || batch?.supplier || '—'}
                      </p>
                    </div>
                    {batch?.notes && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Notas del lote</p>
                        <p className="text-sm text-slate-600 italic" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{batch?.notes}</p>
                      </div>
                    )}
                  </div>
                  {/* Buyer */}
                  {sale?.buyer_name && (
                    <div className="pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Comprador</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{sale?.buyer_name}</p>
                          <button
                            onClick={() => {
                              onClose?.();
                              navigate(`/sales-analysis?buyer=${encodeURIComponent(sale?.buyer_name)}`);
                            }}
                            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 hover:underline"
                            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                          >
                            <Icon name="ExternalLink" size={10} color="currentColor" />
                            Ver análisis
                          </button>
                        </div>
                      </div>
                      {(sale?.buyer_notes || sale?.notes) && (
                        <div>
                          <p className="text-xs text-slate-400 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Notas del comprador</p>
                          <p className="text-sm text-slate-600 italic" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{sale?.buyer_notes || sale?.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              {/* ── Processing Dates Panel ── */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <Icon name="CalendarDays" size={13} color="currentColor" />
                  Fechas de procesamiento
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="space-y-2.5">
                    <DateRow icon="PlusCircle" iconColor="text-blue-500" label="Creado" value={fmtDate(createdAt)} />
                    {processingStarted && (
                      <DateRow icon="Loader" iconColor="text-blue-600" label="Procesamiento iniciado" value={fmtDate(processingStarted)}
                        badge={daysCreatedToProcessing != null ? `+${daysCreatedToProcessing}d desde creación` : null} />
                    )}
                    {readyForSale && (
                      <DateRow icon="CheckCircle" iconColor="text-emerald-500" label="Listo para venta" value={fmtDate(readyForSale)} />
                    )}
                    {soldAt && (
                      <DateRow icon="ShoppingCart" iconColor="text-emerald-600" label="Vendido" value={fmtDate(soldAt)}
                        badge={daysCreatedToSold != null ? `${daysCreatedToSold}d total` : null} />
                    )}
                  </div>

                  {/* Stage durations */}
                  {(daysCreatedToProcessing != null || daysProcessingToSold != null) && (
                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-500 mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Duración por etapa</p>
                      <div className="grid grid-cols-2 gap-2">
                        {daysCreatedToProcessing != null && (
                          <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                            <p className="text-xs text-slate-400" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Creación → Procesamiento</p>
                            <p className="text-sm font-bold font-mono text-slate-700 mt-0.5">{daysCreatedToProcessing}d</p>
                          </div>
                        )}
                        {daysProcessingToSold != null && (
                          <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                            <p className="text-xs text-slate-400" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Procesamiento → Venta</p>
                            <p className="text-sm font-bold font-mono text-slate-700 mt-0.5">{daysProcessingToSold}d</p>
                          </div>
                        )}
                      </div>
                      {/* Deviation badge */}
                      {daysCreatedToSold != null && batch?.processing_days_est != null && (() => {
                        const est = parseFloat(batch?.processing_days_est);
                        const actual = daysCreatedToSold;
                        const diff = actual - est;
                        const onTime = diff <= 0;
                        return (
                          <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            onTime ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            <Icon name={onTime ? 'CheckCircle' : 'Clock'} size={11} color="currentColor" />
                            {onTime ? 'A tiempo' : `Retraso de ${diff}d`}
                            <span className="text-xs opacity-70">(est. {est}d)</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </section>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DateRow({ icon, iconColor, label, value, badge }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Icon name={icon} size={13} color="currentColor" className={iconColor} />
        <span className="text-xs text-slate-600" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {badge && (
          <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{badge}</span>
        )}
        <span className="text-xs font-mono font-semibold text-slate-700">{value}</span>
      </div>
    </div>
  );
}
